/* __next_internal_client_entry_do_not_use__ default auto */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Grid, Typography, useMediaQuery, useTheme } from "__barrel_optimize__?names=Box,Grid,Typography,useMediaQuery,useTheme!=!@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useParams, useRouter } from "next/navigation";
import { useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { ProductApi } from "../../../../../apis/product";
import { useTranslation } from "../../../../i18n/client";
import ConfirmationModal from "../../components/ConfirmationModal";
import FormField from "../../components/FormField";
import PageDescription from "../../components/PageDescription";
import ResponsiveWrapper from "../../components/ResponsiveWrapper";
import ReusableButton from "../../components/ReusableButton";
import { tokens } from "../../theme";
import { ProductSchema, formatStringToEuropeanCurrency, parseNettoPrice, getProductTypeOptions, taxRateOptions, getUnitOptions } from "../helper";
import { useFetchCompanyDetails, useFetchProducts } from "../../../../../data/fetch-hooks";
import { shouldShowErrorModal } from "../../../../../lib/errorUtils";
export default function ManageProduct(param) {
    let { editItem } = param;
    var _companyDetails_companyDetails_companyDetailRecord, _companyDetails_companyDetails;
    const [inProgress, setInProgress] = useState(false);
    const [showError, setShowError] = useState(false);
    const [showDuplicateArticleNumberWarning, setShowDuplicateArticleNumberWarning] = useState(false);
    const [pendingFormData, setPendingFormData] = useState(null);
    const initializedProductIdRef = useRef(null);
    const submitInFlightRef = useRef(false);
    const params = useParams();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const router = useRouter();
    const queryClient = useQueryClient();
    const { t } = useTranslation(params === null || params === void 0 ? void 0 : params.lng, "products");
    const { data: companyDetails = {} } = useFetchCompanyDetails();
    const { data: existingProducts = [], isLoading: productsLoading } = useFetchProducts();
    // Check if company has VAT exemption (no19 or no4)
    const vatLiability = companyDetails === null || companyDetails === void 0 ? void 0 : (_companyDetails_companyDetails = companyDetails.companyDetails) === null || _companyDetails_companyDetails === void 0 ? void 0 : (_companyDetails_companyDetails_companyDetailRecord = _companyDetails_companyDetails.companyDetailRecord) === null || _companyDetails_companyDetails_companyDetailRecord === void 0 ? void 0 : _companyDetails_companyDetails_companyDetailRecord.vat_liability;
    const hasVatExemption = vatLiability === "no19" || vatLiability === "no4";
    const { handleSubmit, formState: { errors }, reset, control, setValue } = useForm({
        resolver: zodResolver(ProductSchema(t)),
        mode: "onBlur",
        defaultValues: {
            article_number: "",
            product_name: "",
            product_type: "",
            description: "",
            unit: "",
            tax_rate: hasVatExemption ? "0" : "",
            netto_price: ""
        }
    });
    const checkForDuplicateArticleNumber = (formData)=>{
        // Try to access products from different possible locations in the response
        const products = (existingProducts === null || existingProducts === void 0 ? void 0 : existingProducts.data) || existingProducts || [];
        // If no products or empty array, no duplicates
        if (!products || products.length === 0) {
            return false;
        }
        // Get current product ID from params or editItem
        let currentProductId = null;
        if (editItem) {
            // Try to get from params first
            if (params === null || params === void 0 ? void 0 : params.productId) {
                const paramId = parseInt(String(params.productId), 10);
                if (!isNaN(paramId)) {
                    currentProductId = paramId;
                }
            }
            // Fallback to editItem.product_id if params doesn't work
            if (currentProductId === null && editItem.product_id !== undefined) {
                const editId = typeof editItem.product_id === "number" ? editItem.product_id : parseInt(String(editItem.product_id || "0"), 10);
                if (!isNaN(editId)) {
                    currentProductId = editId;
                }
            }
        }
        // Filter out current product when editing
        const productsToCheck = currentProductId !== null ? products.filter((product)=>{
            const productId = typeof product.product_id === "number" ? product.product_id : parseInt(String(product.product_id || "0"), 10);
            return isNaN(productId) || productId !== currentProductId;
        }) : products;
        // Check if article_number already exists (case-insensitive comparison)
        const formArticleNumber = String(formData.article_number || "").trim();
        if (!formArticleNumber) {
            return false; // Empty article number, let validation handle it
        }
        return productsToCheck.some((product)=>{
            const productArticleNumber = String(product.article_number || "").trim();
            // Case-insensitive comparison
            return productArticleNumber.toLowerCase() === formArticleNumber.toLowerCase() && productArticleNumber !== "";
        }) || false;
    };
    const submitProduct = async (data)=>{
        if (submitInFlightRef.current) return;
        submitInFlightRef.current = true;
        try {
            setInProgress(true);
            // Split tax_rate into tax_rate and vat_type for API
            const parts = data.tax_rate.split("_");
            const payload = {
                ...data,
                tax_rate: parseInt(parts[0]),
                vat_type: parts[1] || null,
                netto_price: parseNettoPrice(formatStringToEuropeanCurrency(data.netto_price))
            };
            await (editItem ? ProductApi.editProduct(params === null || params === void 0 ? void 0 : params.productId, payload) : ProductApi.createNewProduct(payload));
            await (editItem && queryClient.invalidateQueries({
                queryKey: [
                    "product",
                    params === null || params === void 0 ? void 0 : params.productId
                ]
            }));
            await queryClient.invalidateQueries({
                queryKey: [
                    "products"
                ]
            });
            router.push("/application/products");
        } catch (error) {
            if (shouldShowErrorModal(error)) {
                setShowError(true);
            }
            submitInFlightRef.current = false;
            setInProgress(false);
        }
    };
    const onSubmit = async (data)=>{
        // Skip duplicate check if products are still loading
        if (productsLoading) {
            await submitProduct(data);
            return;
        }
        // Check for duplicate article_number (article numbers should be unique)
        if (checkForDuplicateArticleNumber(data)) {
            setPendingFormData(data);
            setShowDuplicateArticleNumberWarning(true);
            return;
        }
        // No duplicate found, proceed with submission
        await submitProduct(data);
    };
    const handleChangeArticleNumber = ()=>{
        setShowDuplicateArticleNumberWarning(false);
        setPendingFormData(null);
    // Focus will return to the form, user can modify the article number
    };
    useEffect(()=>{
        //Set initial values for edit form - only on initial load, not on refetch
        if (editItem) {
            const paramProductId = (params === null || params === void 0 ? void 0 : params.productId) ? String(params.productId) : "new";
            const currentInitializedId = initializedProductIdRef.current;
            // Only initialize if we haven't initialized for this productId yet
            // This prevents resetting the form when query refetches after submission
            if (currentInitializedId !== paramProductId) {
                // Combine tax_rate and vat_type for display
                const taxRateDisplay = hasVatExemption ? "0" : editItem.vat_type ? "".concat(editItem.tax_rate, "_").concat(editItem.vat_type) : String(editItem.tax_rate);
                reset({
                    ...editItem,
                    tax_rate: taxRateDisplay,
                    netto_price: formatStringToEuropeanCurrency("".concat(editItem.netto_price))
                });
                initializedProductIdRef.current = paramProductId;
            }
        } else if (!editItem) {
            // Reset the ref when editItem becomes undefined (e.g., navigating to new product page)
            initializedProductIdRef.current = null;
        }
    }, [
        editItem,
        hasVatExemption,
        reset,
        params === null || params === void 0 ? void 0 : params.productId
    ]);
    // Auto-set tax rate to 0% when company has VAT exemption
    useEffect(()=>{
        if (hasVatExemption) {
            setValue("tax_rate", "0");
        }
    }, [
        hasVatExemption,
        setValue
    ]);
    return /*#__PURE__*/ _jsxs(ResponsiveWrapper, {
        children: [
            /*#__PURE__*/ _jsx(Box, {
                display: "flex",
                children: /*#__PURE__*/ _jsx(PageDescription, {
                    title: editItem ? t("update_product") : t("new_product")
                })
            }),
            /*#__PURE__*/ _jsxs(Box, {
                width: "100%",
                height: "100%",
                overflow: "scroll",
                pl: "30px",
                pr: "30px",
                pt: "25px",
                paddingBottom: "30px",
                sx: {
                    backgroundColor: "#FFF",
                    borderRadius: "20px"
                },
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "h2",
                        pb: "20px",
                        fontSize: "18px",
                        fontWeight: "500",
                        children: t("information")
                    }),
                    /*#__PURE__*/ _jsxs("form", {
                        onSubmit: handleSubmit(onSubmit),
                        style: {
                            height: "calc(100% - 42px)"
                        },
                        children: [
                            /*#__PURE__*/ _jsxs(Box, {
                                mb: isMobile ? "20px" : "40px",
                                style: {
                                    height: isMobile ? "calc(100% - 88px)" : "calc(100% - 116px)",
                                    overflowY: "scroll",
                                    overflowX: "hidden"
                                },
                                children: [
                                    /*#__PURE__*/ _jsxs(Grid, {
                                        container: true,
                                        spacing: isMobile ? "0px" : "20px",
                                        mb: isMobile ? "20px" : "30px",
                                        children: [
                                            /*#__PURE__*/ _jsx(Grid, {
                                                container: true,
                                                item: true,
                                                xs: 12,
                                                sm: 4,
                                                children: /*#__PURE__*/ _jsx(FormField, {
                                                    title: t("article_number"),
                                                    type: "text",
                                                    placeholder: "",
                                                    name: "article_number",
                                                    error: errors.article_number,
                                                    control: control
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx(Grid, {
                                                container: true,
                                                item: true,
                                                xs: 12,
                                                sm: 4,
                                                mt: isMobile ? "20px" : "0px",
                                                children: /*#__PURE__*/ _jsx(FormField, {
                                                    title: t("name"),
                                                    type: "text",
                                                    placeholder: "",
                                                    name: "product_name",
                                                    error: errors.product_name,
                                                    control: control
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx(Grid, {
                                                container: true,
                                                item: true,
                                                xs: 12,
                                                sm: 4,
                                                mt: isMobile ? "20px" : "0px",
                                                children: /*#__PURE__*/ _jsx(FormField, {
                                                    title: t("type"),
                                                    type: "select",
                                                    placeholder: "",
                                                    name: "product_type",
                                                    error: errors.product_type,
                                                    select: true,
                                                    options: getProductTypeOptions(t),
                                                    control: control
                                                })
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(FormField, {
                                        title: t("description"),
                                        type: "text",
                                        placeholder: "",
                                        name: "description",
                                        error: errors.description,
                                        multiline: true,
                                        rows: 4,
                                        control: control
                                    }),
                                    /*#__PURE__*/ _jsxs(Grid, {
                                        container: true,
                                        spacing: isMobile ? "0px" : "20px",
                                        mt: isMobile ? "20px" : "30px",
                                        children: [
                                            /*#__PURE__*/ _jsx(Grid, {
                                                container: true,
                                                item: true,
                                                xs: 12,
                                                sm: 4,
                                                children: /*#__PURE__*/ _jsx(FormField, {
                                                    title: t("unit"),
                                                    type: "text",
                                                    placeholder: "",
                                                    name: "unit",
                                                    error: errors.unit,
                                                    select: true,
                                                    options: getUnitOptions(t),
                                                    control: control
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx(Grid, {
                                                container: true,
                                                item: true,
                                                xs: 12,
                                                sm: 4,
                                                mt: isMobile ? "20px" : "0px",
                                                children: /*#__PURE__*/ _jsx(FormField, {
                                                    title: t("tax_rate"),
                                                    type: "text",
                                                    placeholder: "",
                                                    name: "tax_rate",
                                                    error: errors.tax_rate,
                                                    select: true,
                                                    options: taxRateOptions,
                                                    control: control,
                                                    disabled: hasVatExemption
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx(Grid, {
                                                container: true,
                                                item: true,
                                                xs: 12,
                                                sm: 4,
                                                mt: isMobile ? "20px" : "0px",
                                                children: /*#__PURE__*/ _jsx(FormField, {
                                                    title: t("net_price"),
                                                    type: "text",
                                                    placeholder: "",
                                                    name: "netto_price",
                                                    transform: formatStringToEuropeanCurrency,
                                                    error: errors.netto_price,
                                                    control: control,
                                                    inputProps: {
                                                        style: {
                                                            textAlign: "right"
                                                        }
                                                    }
                                                })
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("hr", {
                                color: "#A9ABC1",
                                style: {
                                    borderWidth: "0.5px",
                                    marginBottom: isMobile ? "20px" : "40px",
                                    marginTop: "4px"
                                }
                            }),
                            /*#__PURE__*/ _jsxs(Box, {
                                display: "flex",
                                gap: "20px",
                                justifyContent: isMobile ? "space-between" : "flex-end",
                                children: [
                                    /*#__PURE__*/ _jsx(ReusableButton, {
                                        name: t("cancel"),
                                        backgroundColor: colors.button.secondary,
                                        backgroundColorOnHover: colors.button.secondaryOnHover,
                                        color: "#FFF",
                                        type: "button",
                                        width: "100%",
                                        onClick: ()=>router.push("/application/products")
                                    }),
                                    /*#__PURE__*/ _jsx(ReusableButton, {
                                        name: editItem ? t("update") : t("submit"),
                                        disabled: inProgress,
                                        loading: inProgress,
                                        backgroundColor: colors.button.main,
                                        backgroundColorOnHover: colors.button.mainOnHower,
                                        color: "#FFF",
                                        type: "submit",
                                        width: "100%"
                                    })
                                ]
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(ConfirmationModal, {
                title: "Error",
                subtitle: "An unknown error occured. Please try again",
                open: showError,
                handleClose: ()=>setShowError(false),
                primaryBtnName: "OK",
                primaryBtnAction: ()=>setShowError(false)
            }),
            /*#__PURE__*/ _jsx(ConfirmationModal, {
                title: t("duplicate_article_number_warning_title"),
                subtitle: t("duplicate_article_number_warning_message"),
                open: showDuplicateArticleNumberWarning,
                handleClose: handleChangeArticleNumber,
                primaryBtnName: t("change_article_number"),
                primaryBtnAction: handleChangeArticleNumber
            })
        ]
    });
}
